import { FC } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Button,
  useTheme,
} from '@mui/material';
import {
  CalendarToday,
  ArrowForward,
  Event,
  Schedule,
} from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useNavigate } from 'react-router-dom';
import {
  IFinanceUpcomingItem,
  IPeopleUpcomingItem,
  IEventsUpcomingItem,
} from '../../../../../apis/dashboard/dashboard.interfaces';
// Date formatting helper
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

interface UpcomingItemsProps<T extends IFinanceUpcomingItem | IPeopleUpcomingItem | IEventsUpcomingItem> {
  title: string;
  items: T[];
  isLoading?: boolean;
  emptyMessage?: string;
  dataTestId?: string;
  maxItems?: number;
}

const formatUpcomingDate = (date: string): string => {
  const itemDate = new Date(date);
  const today = new Date();
  const diffTime = itemDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Tomorrow';
  } else if (diffDays < 7) {
    return `In ${diffDays} days`;
  } else {
    return formatDate(date);
  }
};

export const UpcomingItems: FC<UpcomingItemsProps<any>> = ({
  title,
  items,
  isLoading = false,
  emptyMessage,
  dataTestId = 'upcoming-items',
  maxItems = 5,
}) => {
  const { translateText } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <Card
        data-testid={dataTestId}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Typography variant="body2" color="textSecondary" textAlign="center">
            {translateText('common.loading')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const displayItems = items?.slice(0, maxItems) || [];

  if (displayItems.length === 0) {
    return (
      <Card
        data-testid={dataTestId}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Typography variant="body2" color="textSecondary" textAlign="center">
            {emptyMessage || translateText('dashboard.upcomingItems.empty')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const handleItemClick = (item: any) => {
    if (item.navigationPath) {
      navigate(item.navigationPath);
    }
  };

  return (
    <Card
      data-testid={dataTestId}
      sx={{
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 2,
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarToday sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>
              {title}
            </Typography>
          </Box>
          {items.length > maxItems && (
            <Chip
              label={`+${items.length - maxItems} more`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>

        <List dense>
          {displayItems.map((item, index) => (
            <ListItem
              key={item.id || index}
              button
              onClick={() => handleItemClick(item)}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              data-testid={`${dataTestId}-item-${item.id || index}`}
            >
              <Box
                sx={{
                  color: theme.palette.primary.main,
                  mr: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  alignSelf: 'flex-start',
                }}
              >
                {item.type?.includes('event') || item.type?.includes('meeting') ? (
                  <Event />
                ) : (
                  <Schedule />
                )}
              </Box>
              <ListItemText
                primary={item.title}
                secondary={
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      {formatUpcomingDate(item.date || item.dueDate || item.startDate)}
                    </Typography>
                    {item.description && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {item.description}
                      </Typography>
                    )}
                    {item.amount && (
                      <Typography variant="caption" color="textSecondary" display="block" fontWeight={600}>
                        {new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                        }).format(item.amount)}
                      </Typography>
                    )}
                  </Box>
                }
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: 500,
                }}
                secondaryTypographyProps={{
                  component: 'div',
                }}
              />
              {item.navigationPath && item.title && item.description && (
                <ListItemSecondaryAction>
                  <Button
                    size="small"
                    endIcon={<ArrowForward />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(item);
                    }}
                    sx={{
                      minWidth: 40,
                      p: 0,
                    }}
                    data-testid={`${dataTestId}-item-${item.id || index}-cta`}
                  />
                </ListItemSecondaryAction>
              )}
            </ListItem>
          ))}
        </List>
      </CardContent>
    </Card>
  );
};
